// My Access — full-page redesign (design bundle: my-access.jsx MyAccessPage).
//
// Header + Active/Expired tabs. The Active tab leads with the "Awaiting
// approval" rail (requests the member submitted), then current access grouped
// by time remaining (≤7d / ≤30d / the rest). Extend lives on each row.
//
// Layout note: the redesigned shell's <main> already supplies the design's
// `.page` gutters (40/24/16 + 80 bottom); we only add the 1440 max-width
// centering + the inline page-header (title row + tabs) here.

import { Box, Flex, Heading } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { TbClockX, TbKey } from "react-icons/tb";
import EmptyState from "components/ds/EmptyState";
import PloyButton from "components/ds/PloyButton";
import Tabs from "components/ds/Tabs";
import Linker from "ui/Linker";
import {
  MF_ACCESS,
  type MFMyActiveRequest,
  type MFSelectableResourceAccess,
} from "state/members/access_v2/catalogs";
import MFMembers from "state/members/membersState";
import AwaitingApprovalRail from "./AwaitingApprovalRail";
import AccessGroup, { accessMetrics } from "./AccessGroup";
import ExpiredAccessList from "./ExpiredAccessList";
import AccessDetailDrawer from "./AccessDetailDrawer";
import RequestDetailDrawer from "./RequestDetailDrawer";

type TabKey = "active" | "expired";

// Loading is handled by the route-level <Suspense> in Members/index.tsx — the
// access/requests queries suspend, so by the time this page renders its data is
// already present (no premature empty-state flash, no in-component skeleton).
const MyAccess = () => {
  const me = MFMembers.useGetMe();
  const { data: access } = MF_ACCESS.useListManagedAccess();
  const { data: requests } = MF_ACCESS.useListMyActiveRequests();
  const [tab, setTab] = useState<TabKey>("active");
  // Only one detail drawer open at a time — opening one dismisses the other.
  const [openAccess, setOpenAccess] = useState<MFSelectableResourceAccess | null>(
    null,
  );
  const [openRequest, setOpenRequest] = useState<MFMyActiveRequest | null>(null);
  const showAccess = (a: MFSelectableResourceAccess) => {
    setOpenRequest(null);
    setOpenAccess(a);
  };
  const showRequest = (r: MFMyActiveRequest) => {
    setOpenAccess(null);
    setOpenRequest(r);
  };

  const expired = useMemo(
    () => access.filter((a) => a.status === "expired"),
    [access],
  );
  const active = useMemo(
    () => access.filter((a) => a.status !== "expired"),
    [access],
  );

  const groups = useMemo(() => {
    const critical: MFSelectableResourceAccess[] = [];
    const warning: MFSelectableResourceAccess[] = [];
    const stable: MFSelectableResourceAccess[] = [];
    for (const a of active) {
      const m = accessMetrics(a);
      if (!m.indefinite && m.daysLeft <= 7) critical.push(a);
      else if (!m.indefinite && m.daysLeft <= 30) warning.push(a);
      else stable.push(a);
    }
    return { critical, warning, stable };
  }, [active]);

  const nothingActive = active.length === 0 && requests.length === 0;

  return (
    <Box maxW="1440px" mx="auto" w="full">
      {/* page header — title row (16px vertical) + tabs */}
      <Box>
        <Flex align="center" py="lg">
          <Heading
            as="h1"
            m={0}
            fontFamily="heading"
            fontWeight={500}
            fontSize="3xl" // 32px
            lineHeight="1"
            letterSpacing="-0.01em"
            color="neutral.1000"
            sx={{ textWrap: "balance" }}
          >
            My Access
          </Heading>
        </Flex>
        <Tabs<TabKey>
          tabs={[
            {
              key: "active",
              label: "Active access",
              count: active.length,
            },
            {
              key: "expired",
              label: "Expired",
              count: expired.length,
            },
          ]}
          activeKey={tab}
          onChange={setTab}
        />
      </Box>

      {tab === "active" && (
        <>
          <AwaitingApprovalRail
            requests={requests}
            me={me}
            onOpen={showRequest}
          />

          {nothingActive ? (
            <Box
              bg="neutral.25"
              borderRadius="component.2xl"
              boxShadow="elevLow"
              py="2xl"
            >
              <EmptyState
                icon={<TbKey />}
                title="No access yet"
                body="You don't have access to anything yet. Browse the catalog to request what you need."
                action={
                  <Linker to="/members/catalog">
                    <PloyButton variant="secondary" size="sm">
                      Request access
                    </PloyButton>
                  </Linker>
                }
              />
            </Box>
          ) : (
            <>
              <AccessGroup
                label="Expiring within 7 days"
                items={groups.critical}
                onOpen={showAccess}
              />
              <AccessGroup
                label="Expiring within 30 days"
                items={groups.warning}
                onOpen={showAccess}
              />
              <AccessGroup
                label="Longer-term access"
                items={groups.stable}
                onOpen={showAccess}
              />
            </>
          )}
        </>
      )}

      {tab === "expired" &&
        (expired.length === 0 ? (
          <Box
            bg="neutral.25"
            borderRadius="component.2xl"
            boxShadow="elevLow"
            py="2xl"
          >
            <EmptyState
              icon={<TbClockX />}
              title="No expired access"
              body="Anything that lapses will appear here so you can renew it."
            />
          </Box>
        ) : (
          <ExpiredAccessList items={expired} onOpen={showAccess} />
        ))}

      <AccessDetailDrawer
        access={openAccess}
        onClose={() => setOpenAccess(null)}
      />
      <RequestDetailDrawer
        request={openRequest}
        onClose={() => setOpenRequest(null)}
      />
    </Box>
  );
};

export default MyAccess;
